import { useEffect, useState } from 'react';
import { getPlView } from '../api/financials_pl';
import { getBsView } from '../api/financials_bs';
import { getPlannedFte } from '../api/hrPlanningFte';

/* Moteur prévisionnel rapide — calcule, à partir du forecast P&L (mensuel) et du
   bilan de clôture N-1, les briques mensuelles : compte de résultat simplifié,
   BFRE et cash flow. Tout sur 12 mois.

   BFRE : composantes lues directement dans le BILAN HYBRIDE (réel ≤ clôture,
   prévision au-delà) — même source que l'onglet BFRE des Rapports, donc les deux
   écrans montrent le même BFRE. Ouverture (mois 0) = clôture réelle 31.12.N-1.
   Variation_m = BFRE_m − BFRE_{m-1}.
   (Jusqu'au 2026-08-09, les mois futurs étaient réécrits ici par une projection
   run-rate maison — créances = CA × 12/365 × DSO, avec des dénominateurs DPO/DIO
   inversés par rapport au modèle officiel — qui divergeait du bilan en silence.)
   Cash : impact_m = −ΔBFRE_m ; cash flow opérationnel_m = EBITDA_m + impact_m ;
   trésorerie cumulée_m = liquidités N-1 + Σ cash flow opérationnel. */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const monthsArr = (row) => Array.from({ length: 12 }, (_, i) => num(row?.months?.[String(i + 1)]));
const findCode = (rows, code) => (rows || []).find((r) => r.code === code) || null;
// Lignes du bilan repérées par leur CLÉ STANDARD (std_key), jamais par leur
// libellé : renommer « Liquidité » ou « Créances-clients » ne doit rien casser.
// Une ligne ajoutée par le tenant n'a pas de clé → aucun driver, report du
// dernier solde réel (pas de repli deviné).
const findStd = (rows, key) => (rows || []).find((r) => r.type === 'detail' && r.std_key === key) || null;

const STD_LIQ = 'liquidity';
const LIAB = new Set(['current_liabilities', 'long_term_liabilities']);

export function useForecastData(tenantId, year) {
  const [plView, setPlView] = useState(null);
  const [bsCur, setBsCur] = useState(null);
  const [bsPrev, setBsPrev] = useState(null);
  const [bsPrev2, setBsPrev2] = useState(null);
  const [plPrev, setPlPrev] = useState(null);
  const [plPrev2, setPlPrev2] = useState(null);
  const [fteCur, setFteCur] = useState(null);
  const [ftePrev, setFtePrev] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || !year) {
      setPlView(null); setBsCur(null); setBsPrev(null); setBsPrev2(null);
      setPlPrev(null); setPlPrev2(null); setFteCur(null); setFtePrev(null); return;
    }
    let alive = true;
    setLoading(true);
    Promise.all([
      getPlView(tenantId, year),
      getBsView(tenantId, year, { flatForecast: true }).catch(() => null),
      getBsView(tenantId, year - 1).catch(() => null),
      getBsView(tenantId, year - 2).catch(() => null),
      getPlView(tenantId, year - 1).catch(() => null),
      getPlView(tenantId, year - 2).catch(() => null),
      getPlannedFte(tenantId, year).catch(() => null),
      getPlannedFte(tenantId, year - 1).catch(() => null),
    ]).then(([p, bc, b, b2, pp, pp2, fc, fp]) => {
      if (!alive) return;
      setPlView(p?.data || null);
      setBsCur(bc?.data || null);
      setBsPrev(b?.data || null);
      setBsPrev2(b2?.data || null);
      setPlPrev(pp?.data || null);
      setPlPrev2(pp2?.data || null);
      setFteCur(fc?.data || null);
      setFtePrev(fp?.data || null);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, year]);

  return { plView, bsCur, bsPrev, bsPrev2, plPrev, plPrev2, fteCur, ftePrev, loading };
}

// Variation BFRE annuelle d'une année (réel) : Σ is_bfre (clôture déc, signé)
// − Σ is_bfre (clôture déc de l'année précédente). null si données absentes.
function bfreVarYear(bsView, bsPrevView) {
  if (!bsView) return null;
  const lines = (bsView.lines || []).filter((l) => l.is_bfre);
  if (!lines.length) return null;
  const byCode = (v) => { const o = {}; (v?.rows || []).filter((r) => r.type === 'detail').forEach((r) => { o[r.code] = r; }); return o; };
  const cur = byCode(bsView), prev = byCode(bsPrevView);
  let close = 0; let open = 0; let hasOpen = !!bsPrevView;
  lines.forEach((l) => {
    const sgn = LIAB.has((cur[l.id] || l).section || l.section) ? -1 : 1;
    close += sgn * num(cur[l.id]?.months?.['12']);
    open += sgn * num(prev[l.id]?.months?.['12']);
  });
  return hasOpen ? close - open : null;
}

/* `assumptions` (DSO/DPO/DIO/acomptes) n'est plus lu : les composantes du BFRE
   viennent du bilan hybride, qui applique déjà ces hypothèses côté serveur.
   Le paramètre est conservé pour ne pas casser les appelants. */
export function buildForecastModel(plView, bsCur, bsPrev, plPrev, assumptions, extra = {}) {
  if (!plView) return null;
  const { bsPrev2 = null, fteCur = null, ftePrev = null } = extra;
  const rows = plView.rows || [];
  const closedCount = Number.isFinite(plView.closed_count) ? plView.closed_count : 12;
  const secM = (code) => monthsArr(findCode(rows, `sec:${code}`));
  const revenue = secM('revenue');   // = Chiffre d'affaires net (total section)
  // Décomposition du CA par type de revenu (marqueur revenue_type des lignes) :
  // prestations / marchandises / autres. null → 'autres'.
  const revByType = (allRows, type) => {
    const ds = (allRows || []).filter((r) => r.type === 'detail' && r.section === 'revenue' && (r.revenue_type || 'autres') === type);
    return Array.from({ length: 12 }, (_, i) => ds.reduce((a, r) => a + num(r.months?.[String(i + 1)]), 0));
  };
  const revPrestations = revByType(rows, 'prestations');
  const revMarchandises = revByType(rows, 'marchandises');
  const revAutres = revByType(rows, 'autres');
  const cogs = secM('cogs');
  const personnel = secM('personnel');
  const indirect = secM('other_opex');
  const addArr = (a, b) => a.map((v, i) => v + b[i]);
  const valueAdded = addArr(revenue, cogs);
  const grossMargin = addArr(valueAdded, personnel);
  const ebitda = addArr(grossMargin, indirect);
  // « Autre » du cash flow = résultat sous l'EBITDA (financier + hors exploitation
  // + impôts) — l'amortissement, non-cash, s'annule (charge puis addback). C'est
  // le résiduel qui amène l'EBITDA au cash flow opérationnel (méthode indirecte,
  // alignée sur l'onglet Flux). Le résultat avant variation BFR.
  const financial = secM('financial');                        // résultat financier (charge d'intérêt dominante)
  const autre = addArr(addArr(financial, secM('non_operating')), secM('tax'));
  // Amortissements (non-cash) + résultat net = base du cash flow indirect « depuis
  // le résultat net » : NR + addback amortissements + ± BFR = cash flow opérationnel.
  const depreciation = secM('depreciation');
  const amort = depreciation.map((v) => -v);                 // addback (charge négative → +)
  const netResult = ebitda.map((e, i) => e + depreciation[i] + autre[i]);

  // Ouverture = clôture 31.12.N-1 (bilan N-1, solde décembre).
  // Ouverture trésorerie = liquidités clôture 31.12.N-1.
  const bsRows = bsPrev?.rows || [];
  const oLiq = findStd(bsRows, STD_LIQ) ? num(findStd(bsRows, STD_LIQ).total) : null;

  const revFy = revenue.reduce((a, b) => a + b, 0);

  // ── BFRE aligné sur Finance : composantes = lignes marquées is_bfre du bilan,
  //    signe par section (actif + / passif −). Les 12 mois viennent du BILAN
  //    HYBRIDE (réel ≤ clôture, prévision au-delà) — la même source que l'onglet
  //    BFRE des Rapports, donc les deux écrans affichent le même BFRE.
  //    Colonne d'ouverture = clôture 31.12.N-1.
  //    (Avant le 2026-08-09, les mois futurs étaient RÉÉCRITS par une projection
  //    run-rate maison — créances = CA × 12/365 × DSO — qui divergeait du bilan
  //    sans que rien ne le signale.)
  const bcByCode = {}; (bsCur?.rows || []).filter((r) => r.type === 'detail').forEach((r) => { bcByCode[r.code] = r; });
  const bpByCode = {}; (bsPrev?.rows || []).filter((r) => r.type === 'detail').forEach((r) => { bpByCode[r.code] = r; });
  const bfreLines = (bsCur?.lines || bsPrev?.lines || []).filter((l) => l.is_bfre);

  const components = bfreLines.map((l) => {
    const row = bcByCode[l.id] || bpByCode[l.id];
    const sgn = LIAB.has(row?.section || l.section) ? -1 : 1;
    const realRow = bcByCode[l.id];
    const real = realRow ? monthsArr(realRow).map((x) => sgn * num(x)) : null;
    const open = sgn * num(bpByCode[l.id]?.months?.['12']);
    // Ligne absente du bilan de l'année : report du dernier solde connu, jamais
    // une valeur reconstituée.
    const carry = (closedCount > 0 && real) ? real[closedCount - 1] : open;
    const vals = Array.from({ length: 12 }, (_, i) => (real ? real[i] : carry));
    return { label: l.label, stdKey: l.std_key || null, sgn, open, vals };
  });

  const bfre = components.length ? Array.from({ length: 12 }, (_, i) => {
    const present = components.map((c) => c.vals[i]).filter((v) => v != null);
    return present.length ? present.reduce((a, b) => a + b, 0) : null;
  }) : null;
  const openBfre = components.length ? components.reduce((a, c) => a + num(c.open), 0) : null;
  const dBfre = bfre ? bfre.map((b, i) => {
    if (b == null) return null;
    const prev = i === 0 ? num(openBfre) : (bfre[i - 1] == null ? null : bfre[i - 1]);
    return prev == null ? null : b - prev;
  }) : null;
  const cashImpact = dBfre ? dBfre.map((d) => (d == null ? null : -d)) : null;

  // ── Cash flow complet, RÉCONCILIÉ sur la trésorerie réelle (bilan N) ─────────
  // Mois CLÔTURÉS : décomposition ancrée sur le bilan → opérationnel +
  // investissement + financement = variation RÉELLE des liquidités. L'opérationnel
  // intègre tout le fonds de roulement (BFRE restreint + « autres éléments » = le
  // reste de l'actif/passif circulant). L'investissement = −Δ immobilisations. Le
  // financement est le solde (plug) : dettes LT, capital, dividendes, résiduel.
  // Mois FORECAST : seul l'opérationnel projeté (EBITDA + Autre + ± BFRE) ;
  // autres éléments FR / investissement / financement = absents (0, non projetés).
  const secBs = (code) => { const r = findCode(bsCur?.rows || [], code); return r ? monthsArr(r).map(num) : null; };
  const decN1 = (code) => num(findCode(bsPrev?.rows || [], code)?.months?.['12']);
  const liqRow = findStd(bsCur?.rows || [], STD_LIQ);
  const liqAct = liqRow ? monthsArr(liqRow).map(num) : null;
  const caAct = secBs('sec:current_assets');
  const clAct = secBs('sec:current_liabilities');
  const faAct = secBs('sec:fixed_assets');
  const caExLiq = caAct && liqAct ? caAct.map((v, i) => v - liqAct[i]) : null;
  const caExLiqN1 = num(decN1('sec:current_assets')) - num(oLiq);
  const bsOk = liqAct && caExLiq && clAct && faAct;

  // ── Détail du fonds de roulement PAR LIGNE de bilan (impact cash mensuel).
  // Chaque poste circulant (hors liquidité) : impact = −Δ(actif) ou +Δ(passif).
  // Somme = variation totale du FR = ce que l'opérationnel absorbe. Sépare les
  // lignes marquées is_bfre (BFRE) des autres (« autres éléments du FR »).
  // Le classement suit le TAG `cf_category` du bilan (même dimension que le
  // Flux de trésorerie des Rapports) : une ligne circulante taguée financement
  // (fin_debt, ex. C/C actionnaires) ou capitaux propres (equity) N'EST PAS du
  // fonds de roulement — sortie d'ici, elle retombe dans le flux de financement
  // (calculé en résidu de la variation de liquidité), comme côté serveur.
  const WC_SECS = new Set(['current_assets', 'current_liabilities']);
  const FIN_CATS = new Set(['fin_debt', 'equity']);
  const wcRows = (bsCur?.lines || [])
    .filter((l) => WC_SECS.has(l.section) && l.std_key !== 'liquidity'
      && !FIN_CATS.has(l.cf_category))
    .map((l) => {
      const row = bcByCode[l.id];
      if (!row) return null;
      const sgn = l.section === 'current_liabilities' ? -1 : 1;   // actif + / passif −
      const bal = monthsArr(row).map((x) => sgn * num(x));
      const openBal = sgn * num(bpByCode[l.id]?.months?.['12']);
      const cash = Array.from({ length: 12 }, (_, i) => {
        const prev = i === 0 ? openBal : bal[i - 1];
        return -(bal[i] - prev);   // hausse d'actif = sortie de cash · hausse de passif = entrée
      });
      return { label: l.label, isBfre: !!l.is_bfre, cash };
    })
    .filter(Boolean);
  const wcSum = (pred) => Array.from({ length: 12 }, (_, i) =>
    wcRows.filter(pred).reduce((s, r) => s + (r.cash[i] || 0), 0));
  const bfreCash = wcSum((r) => r.isBfre);        // impact cash du BFRE (par ligne bilan)
  const autresCash = wcSum((r) => !r.isBfre);     // impact cash des autres éléments du FR

  // Décomposition ANCRÉE sur le bilan pour TOUS les mois (le bilan est désormais
  // projeté via flatForecast) → opérationnel + investissement + financement =
  // variation RÉELLE des liquidités du bilan, y compris en prévisionnel. C'est ce
  // qui réconcilie le cash flow indirect avec le Bilan sur toute la période.
  const autresFR = [], investing = [], financing = [], opCf = [], total = [];
  const dImmob = [], amortInv = [];   // détail investissement : Δ immob (bilan) · amort retraité
  for (let i = 0; i < 12; i++) {
    const ci = cashImpact ? cashImpact[i] : null;
    if (bsOk) {
      const pFa = i === 0 ? num(decN1('sec:fixed_assets')) : faAct[i - 1];
      const pLiq = i === 0 ? num(oLiq) : liqAct[i - 1];
      // BFRE et autres éléments du FR = impacts cash PAR LIGNE du bilan (somme =
      // variation totale du FR). Cohérent avec la liquidité du bilan → réconcilie.
      const ciB = bfreCash[i];
      const aFR = autresCash[i];
      // Investissement = −CAPEX = −(Δ immobilisations NETTES + amortissements). On
      // distingue la variation du solde bilan de l'amortissement (non-cash, déjà
      // rajouté en opérationnel) pour un flux d'investissement juste.
      const dIm = faAct[i] - pFa;
      const amI = num(amort[i]);
      const inv = -(dIm + amI);
      const op = ebitda[i] + autre[i] + ciB + aFR;
      const tot = liqAct[i] - pLiq;
      autresFR.push(aFR); investing.push(inv); opCf.push(op); financing.push(tot - op - inv); total.push(tot);
      dImmob.push(dIm); amortInv.push(amI);
    } else if (ci == null) {
      autresFR.push(null); investing.push(null); opCf.push(null); financing.push(null); total.push(null);
      dImmob.push(null); amortInv.push(null);
    } else {
      const op = ebitda[i] + autre[i] + ci;     // forecast : opérationnel projeté seul
      autresFR.push(0); investing.push(0); financing.push(0); opCf.push(op); total.push(op);
      dImmob.push(0); amortInv.push(0);
    }
  }

  // Trésorerie fin de mois : mois clos = liquidités RÉELLES ; forecast = report +
  // variation totale (= opérationnel projeté).
  let runLiq = null;
  {
    const anchor = (liqAct && closedCount > 0) ? liqAct[closedCount - 1] : oLiq;
    if (anchor != null) {
      runLiq = []; let c = anchor;
      for (let i = 0; i < 12; i++) {
        if (i < closedCount) { runLiq.push(liqAct ? liqAct[i] : null); continue; }
        if (total[i] == null) { runLiq.push(null); continue; }
        c += total[i]; runLiq.push(c);
      }
    }
  }

  const pRows = plPrev?.rows || [];

  // ── Comparatif N-1 (totaux année pleine du réel N-1) ───────────────────────
  const hasPrev = !!plPrev;
  const secP = (code) => num(findCode(pRows, `sec:${code}`)?.total);
  const revN1 = secP('revenue'), cogsN1 = secP('cogs'), persN1 = secP('personnel'), indN1 = secP('other_opex');
  const vaN1 = revN1 + cogsN1, mbN1 = vaN1 + persN1, ebitdaN1 = mbN1 + indN1;
  // CA N-1 par type (totaux année pleine des lignes détail revenue).
  const revTypeN1 = (type) => (pRows || [])
    .filter((r) => r.type === 'detail' && r.section === 'revenue' && (r.revenue_type || 'autres') === type)
    .reduce((a, r) => a + num(r.total), 0);
  const prestN1 = revTypeN1('prestations'), marchN1 = revTypeN1('marchandises'), autresN1 = revTypeN1('autres');
  const pnlN1 = hasPrev
    ? { revenue: revN1, revPrestations: prestN1, revMarchandises: marchN1, revAutres: autresN1,
        cogs: cogsN1, valueAdded: vaN1, personnel: persN1, grossMargin: mbN1, indirect: indN1, ebitda: ebitdaN1 }
    : null;
  // Variation BFRE N-1 (réel) et cash flow opérationnel N-1.
  const dBfreN1 = bfreVarYear(bsPrev, bsPrev2);
  const cashImpactN1 = dBfreN1 == null ? null : -dBfreN1;
  const financialN1 = secP('financial');
  const autreN1 = financialN1 + secP('non_operating') + secP('tax');
  const deprN1 = secP('depreciation');
  const amortN1 = -deprN1;
  const netResultN1 = ebitdaN1 + deprN1 + autreN1;
  const opCfN1 = cashImpactN1 == null ? null : ebitdaN1 + cashImpactN1 + autreN1;
  // Cash flow N-1 (réel année pleine) — même décomposition bilan, clôtures N-1/N-2.
  const decN2 = (code) => num(findCode(bsPrev2?.rows || [], code)?.months?.['12']);
  const liqN1Dec = oLiq;
  const liqN2Row = findStd(bsPrev2?.rows || [], STD_LIQ);
  const liqN2Dec = liqN2Row ? num(liqN2Row.months?.['12']) : null;
  const bsN1Ok = bsPrev2 && liqN1Dec != null && liqN2Dec != null;
  let autresFRN1 = null, investingN1 = null, financingN1 = null, totalN1 = null, opCfFullN1 = null;
  if (bsN1Ok) {
    let fullWcN1 = -(((decN1('sec:current_assets') - liqN1Dec) - (decN2('sec:current_assets') - liqN2Dec))
      - (decN1('sec:current_liabilities') - decN2('sec:current_liabilities')));
    // Même règle qu'en N : les lignes circulantes taguées financement (C/C
    // actionnaires…) sortent du fonds de roulement → le résidu de financement
    // N-1 les récupère.
    (bsPrev?.lines || [])
      .filter((l) => WC_SECS.has(l.section) && FIN_CATS.has(l.cf_category))
      .forEach((l) => {
        const sgn = l.section === 'current_liabilities' ? -1 : 1;
        const d1 = sgn * num(findCode(bsPrev.rows || [], l.id)?.months?.['12']);
        const d2 = sgn * num(findCode(bsPrev2?.rows || [], l.id)?.months?.['12']);
        fullWcN1 += (d1 - d2);   // retire l'impact cash −(Δ solde signé) du FR
      });
    autresFRN1 = cashImpactN1 == null ? null : fullWcN1 - cashImpactN1;
    investingN1 = -(decN1('sec:fixed_assets') - decN2('sec:fixed_assets'));
    totalN1 = liqN1Dec - liqN2Dec;
    opCfFullN1 = ebitdaN1 + autreN1 + fullWcN1;
    financingN1 = totalN1 - opCfFullN1 - investingN1;
  }

  // ── EPT (Planification personnel) — mensuel + moyenne, N-1 ──────────────────
  const eptMonths = fteCur?.months ? Array.from({ length: 12 }, (_, i) => num(fteCur.months[String(i + 1)])) : null;
  const eptAvg = fteCur?.avg != null ? num(fteCur.avg) : null;
  const eptAvgN1 = ftePrev?.avg != null ? num(ftePrev.avg) : null;
  // Revenu/EPT basé sur les PRESTATIONS (activité de service liée à l'EPT),
  // pas sur le CA total (les marchandises ne consomment pas d'EPT).
  const prestFy = revPrestations.reduce((a, b) => a + b, 0);
  const eptOk = eptMonths && eptMonths.some((v) => v > 0);
  // Revenus prestations / EPT du mois (brut) et annualisé (× 12 ÷ EPT du mois).
  const revPerEptMonths = eptOk ? revPrestations.map((v, i) => (eptMonths[i] > 0 ? v / eptMonths[i] : null)) : null;
  const revPerEptAnnMonths = eptOk ? revPrestations.map((v, i) => (eptMonths[i] > 0 ? (v * 12) / eptMonths[i] : null)) : null;
  const revPerEptTot = eptAvg ? prestFy / eptAvg : null;       // annuel (= annualisé)
  const revPerEptN1 = eptAvgN1 && prestN1 ? prestN1 / eptAvgN1 : null;
  // KPI « à fin de mois clôturé » : EPT spot (dernier mois clos) + prestations
  // cumulées annualisées (× 12 / mois clos) rapportées à cet EPT.
  const eptSpot = (eptMonths && closedCount > 0) ? eptMonths[closedCount - 1] : null;
  const prestYtdAnnualized = closedCount > 0
    ? revPrestations.slice(0, closedCount).reduce((a, b) => a + b, 0) * 12 / closedCount : null;
  const revPerEptSpot = (eptSpot && prestYtdAnnualized != null) ? prestYtdAnnualized / eptSpot : null;
  // KPI « à venir » (mois prévisionnels) : prestations des mois futurs annualisées
  // (× 12 / nb mois restants) rapportées à l'EPT moyen de ces mois.
  const nFwd = 12 - closedCount;
  const eptFwd = (eptMonths && nFwd > 0) ? eptMonths.slice(closedCount).filter((v) => v > 0) : null;
  const eptFwdAvg = eptFwd && eptFwd.length ? eptFwd.reduce((a, b) => a + b, 0) / eptFwd.length : null;
  const prestFwdAnnualized = nFwd > 0
    ? revPrestations.slice(closedCount).reduce((a, b) => a + b, 0) * 12 / nFwd : null;
  const revPerEptForward = (eptFwdAvg && prestFwdAnnualized != null) ? prestFwdAnnualized / eptFwdAvg : null;
  // Croissance annuelle du chiffre d'affaires (total N vs total N-1).
  const revGrowth = (revN1 && revN1 !== 0) ? (revFy - revN1) / revN1 : null;

  return {
    closedCount,
    pnl: { revenue, revPrestations, revMarchandises, revAutres, cogs, valueAdded, personnel, grossMargin, indirect, ebitda },
    pnlN1,
    wc: { components, bfre, openBfre, dBfre, dBfreN1 },
    cash: { ebitda, netResult, amort, financial, financialN1, cashImpact, bfreCash, autresCash, wcRows, dImmob, amortInv, autre, autreN1, autresFR, investing, financing, opCf, total, oLiq, runLiq,
            netResultN1, amortN1, opCfN1, opCfFullN1, autresFRN1, investingN1, financingN1, totalN1 },
    kpi: { eptMonths, eptAvg, eptAvgN1, eptSpot, revPerEptMonths, revPerEptAnnMonths,
           revPerEptTot, revPerEptN1, revPerEptSpot, revPerEptForward, revGrowth,
           revFy, revN1, prestFy, prestN1 },
  };
}

// Somme des 12 mois en ignorant les cellules absentes (null) ; null si tout absent.
export const sum12 = (arr) => {
  if (!arr) return null;
  const present = arr.filter((v) => v != null);
  return present.length ? present.reduce((a, b) => a + Number(b), 0) : null;
};

// Cascade de trésorerie (CHF) commune au Cash flow et à la Synthèse : mois clos =
// réel ancré bilan, prévision = opérationnel + investissement + financement (lignes
// saisies). Renvoie les sous-totaux invest/financement, la variation totale et les
// soldes de trésorerie début/fin de mois.
export function buildTreasury(c, cashflow, cc) {
  const idx = Array.from({ length: 12 }, (_, i) => i);
  // Investissement / financement LIÉS AU BILAN sur toute la période (plus de
  // saisie manuelle) : Δ immobilisations (hors amort) · Δ dettes LT + capitaux
  // propres hors résultat. Le bilan étant projeté, ces flux existent aussi en
  // prévisionnel.
  const investSub = idx.map((i) => c.investing[i]);
  const financingSub = idx.map((i) => c.financing[i]);
  const totalVar = idx.map((i) => c.total[i]);
  const oLiq = c.oLiq;
  const run = [];
  for (let i = 0; i < 12; i++) {
    if (i < cc) { run.push(c.runLiq ? c.runLiq[i] : null); continue; }
    const prev = i === 0 ? oLiq : run[i - 1];
    run.push(prev == null || totalVar[i] == null ? null : prev + totalVar[i]);
  }
  const soldeInit = idx.map((i) => (i === 0 ? oLiq : run[i - 1]));
  return { investSub, financingSub, totalVar, soldeInit, soldeFin: run, oLiq };
}
